import { memo } from 'react';
import { getTooltipStyle, LABEL_STYLE } from '../shared/constants';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useResponsiveYAxisWidth } from '../shared/useResponsiveYAxisWidth';
import { useInnovationData } from './useInnovationData';

const LEVELS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

export const InnovationPackageChart = memo(function InnovationPackageChart() {
  const { packageAnalysisData, innovationResults } = useInnovationData();
  const yAxisWidth = useResponsiveYAxisWidth(150);
  if (!packageAnalysisData) return (
    <ChartWrapper title="Innovation Package Analysis">
      <NoChartData message="No Innovation Package results available" detail="This chart analyzes IPSR assessment data — core innovation readiness, complementary innovations, partner organizations, and impact measures. The current selection has no Innovation Package results." />
    </ChartWrapper>
  );

  const {
    totalPackages, withComplementary, avgComplementary, orgTypeCounts, orgTypeCount,
    coreReadinessDist, coreUseDist, totalHectares,
  } = packageAnalysisData;

  const levelData = LEVELS
    .map(l => ({
      level: `Level ${l}`,
      readiness: coreReadinessDist[l] || 0,
      use: coreUseDist[l] || 0,
    }))
    .filter(d => d.readiness > 0 || d.use > 0);

  const orgData = Object.entries(orgTypeCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  const exportData = [
    { Metric: 'Total Innovation Packages', Value: totalPackages },
    { Metric: 'With complementary innovations', Value: withComplementary },
    { Metric: 'Avg complementary per package', Value: avgComplementary },
    { Metric: 'Organization types reached', Value: orgTypeCount },
    ...(totalHectares > 0 ? [{ Metric: 'Total hectares', Value: totalHectares }] : []),
    ...LEVELS.filter(l => coreReadinessDist[l] || coreUseDist[l]).map(l => ({
      Metric: `Level ${l}`,
      'Core Readiness': coreReadinessDist[l] || 0,
      'Core Use': coreUseDist[l] || 0,
    })),
    ...orgData.map(d => ({ Metric: `Org: ${d.name}`, Value: d.count })),
  ];

  return (
    <ChartWrapper title="Innovation Package Analysis" info="Analysis of Innovation Packages: core innovation readiness vs use levels, and the types of organizations reached." chartData={exportData} exportFilename="innovation-packages" sourceResults={innovationResults}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <div className="bg-pink-50 rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-pink-700">{totalPackages}</p>
          <p className="text-sm text-pink-600">Packages</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-purple-700">{withComplementary}</p>
          <p className="text-sm text-purple-600">With complementary</p>
        </div>
        <div className="bg-indigo-50 rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-indigo-700">{avgComplementary}</p>
          <p className="text-sm text-indigo-600">Avg complementary</p>
        </div>
        <div className="bg-teal-50 rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-teal-700">{orgTypeCount}</p>
          <p className="text-sm text-teal-600">Org types reached</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {levelData.length > 0 && (
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-2">Core Innovation Readiness vs Use Levels</h5>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={levelData} margin={{ left: 0, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="level" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.6 }}>
                      <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>{label}</div>
                      {payload.map(p => (
                        <div key={p.dataKey} style={{ color: p.color }}>{p.name}: {p.value}</div>
                      ))}
                    </div>
                  );
                }} />
                <Legend />
                <Bar dataKey="readiness" fill="#8b5cf6" name="Readiness" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="readiness" position="top" style={LABEL_STYLE} />
                </Bar>
                <Bar dataKey="use" fill="#22c55e" name="Use" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="use" position="top" style={LABEL_STYLE} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {orgData.length > 0 && (
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-2">Organization Types Reached</h5>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={orgData} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: yAxisWidth < 150 ? 9 : 11 }} width={yAxisWidth} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.[0]) return null;
                  const d = payload[0].payload;
                  return (
                    <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.4 }}>
                      <div style={{ fontWeight: 600, color: '#1e293b' }}>{d.name}</div>
                      <div style={{ color: '#4b5563' }}>{d.count} innovation{d.count !== 1 ? 's' : ''}</div>
                    </div>
                  );
                }} />
                <Bar dataKey="count" fill="#ec4899" radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="count" position="right" style={LABEL_STYLE} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {totalHectares > 0 && (
        <div className="mt-3 text-sm text-gray-500 text-center">
          Total hectares covered by Innovation Packages: <strong>{totalHectares.toLocaleString()}</strong>
        </div>
      )}
    </ChartWrapper>
  );
});
